import React from "react";
import {Box, Flex, Avatar, Text, Link, Icon, Heading, Button} from "@chakra-ui/react";
import { FaLinkedin, FaGithub, FaTwitter } from "react-icons/fa";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Author {
    name: string;
    role?: string;
    bio: string;
    inProject?: string;
    image: string;
    socials?: {
        linkedin?: string;
        github?: string;
        twitter?: string;
    };
}

const authors: Author[] = [
    {
        name: "Anna Krasodomska",
        role: "Software Engineer,\n Full-stack Developer",
        bio: "Odpowiedzialna za zebranie i opracowanie wymagań, opracowanie dokumentacji, prace nad schematem bazy danych.\nWspótwórca backendu i frontendu.",
        inProject: "W projekcie od sierpnia 2024.",
        image: "/images/ania.jpg",
        socials: {
            linkedin: "https://www.linkedin.com/in/anna-krasodomska-78a734313/",
            github: "https://github.com/KrasodomskaAnna",
        },
    },
    {
        name: "Łukasz Głazik",
        role: "Backend Developer",
        bio: "Odpowiedzialny za system uwierzytelniania użytkowników oraz system kolejkowania zadań w aplikacji. Współtwórca backendu, oraz osoba odpowiedzialna za konfigurację środowiska.",
        inProject: "W projekcie we wrześniu i od grudnia 2024.",
        image: "/images/lukgla.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/lukas-głazik-7962b2244/",
            github: "https://github.com/lukgla",
        },
    },
    {
        name: "Paweł Marczuk",
        role: "Frontend Developer",
        bio: "Odpowiedzialny za rozwijanie aplikacji frontendowej we frameworku React.",
        inProject: "W projekcie od października 2024.",
        image: "/images/marczuk.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/pawel-marczuk/",
            github: "https://github.com/pawelmarczuk",
        },
    },
    {
        name: "Tomasz Szcząchor",
        role: "Full-stack Developer",
        bio: "Odpowiedzialny za projekt\ni wykonanie UI aplikacji\noraz rozwijanie aplikacji frontendowej\nwe frameworku React.\nWspółtwórca logiki komunikacji aplikacji frontendowej z backendem.",
        inProject: "W projekcie od września do października 2024.",
        image: "/images/tomasz.png",
        socials: {
            linkedin: "https://www.linkedin.com/in/tomasz-szcząchor-44747429b/",
            github: "https://github.com/tomSzczach",
        },
    },
    {
        name: "Piotr Malinowski",
        role: "Product Manager",
        bio: "Koordynator prac. Odpowiedzialny za zwiększanie wartości biznesowej projektu.",
        inProject: "W projekcie od października 2024.",
        image: "/images/maliniak.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/malinowskipiotr/",
        },
    },
    {
        name: "Bartosz Błyszcz",
        bio: "Konsultacje dotyczące diagramu klas aplikacji.",
        inProject: "W projekcie w listopadzie 2024.",
        image: "/images/bartek.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/bartosz-blyszcz/",
        },
    },
    {
        name: "Bartłomiej Suchecki",
        bio: "Konsultacje dotyczące diagramu klas aplikacji.",
        inProject: "W projekcie w listopadzie 2024.",
        image: "/images/suchar.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/suchyba/",
        },
    },
    {
        name: "Adam Skarżyński",
        bio: "Odpowiedzialny za infrastrukturę projektu.",
        image: "/images/adam.jpeg",
        socials: {
            linkedin: "https://www.linkedin.com/in/adam-skarżyński-416442142/",
        },
    },
];

const AuthorCard: React.FC<{ author: Author; position: number }> = ({ author, position }) => {
    // Efekty zależne od pozycji w karuzeli
    const scaleMap = [0.7, 0.85, 1, 0.85, 0.7]; // Skala kart (środkowa największa)
    const blurMap = ["6px", "3px", "0px", "3px", "6px"]; // Stopień rozmycia
    const opacityMap = [0.7, 0.85, 1, 0.85, 0.7]; // Przezroczystość

    return (
        <motion.div
            style={{
                scale: scaleMap[position],
                filter: `blur(${blurMap[position]})`,
                opacity: opacityMap[position],
                transition: "all 0.5s ease-in-out",
            }}
        >
            <Box
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                padding="6"
                textAlign="center"
                minWidth="200px"
                maxWidth="250px"
                bg="white"
                boxShadow="xl"
                mx="2"
            >
                <Avatar src={author.image} size="xl" name={author.name} mb="4" />
                <Text fontWeight="bold" fontSize="lg">
                    {author.name}
                </Text>
                <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                    {author.role}
                </Text>
                <Text mt="2" fontSize="sm" whiteSpace="pre-line">
                    {author.bio}
                </Text>
                <Text mt="2" fontSize="sm" color="gray.400" fontStyle="italic" whiteSpace="pre-line">
                    {author.inProject}
                </Text>
                <Flex justifyContent="center" mt="4" gap="4">
                    {author.socials?.linkedin && (
                        <Link href={author.socials.linkedin} isExternal>
                            <Icon as={FaLinkedin} w="5" h="5" />
                        </Link>
                    )}
                    {author.socials?.github && (
                        <Link href={author.socials.github} isExternal>
                            <Icon as={FaGithub} w="5" h="5" />
                        </Link>
                    )}
                    {author.socials?.twitter && (
                        <Link href={author.socials.twitter} isExternal>
                            <Icon as={FaTwitter} w="5" h="5" />
                        </Link>
                    )}
                </Flex>
            </Box>
        </motion.div>
    );
};

const AuthorCarousel = () => {
    const [index, setIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const indices = [
        (index - 2 + authors.length) % authors.length,
        (index - 1 + authors.length) % authors.length,
        index % authors.length,
        (index + 1) % authors.length,
        (index + 2) % authors.length,
    ];

    useEffect(() => {
        if (isPaused) return;
        const interval = setInterval(() => {
            shiftRight();
        }, 3000);

        return () => clearInterval(interval);
    }, [isPaused, index]);

    const shiftRight = () => {
        setIndex((prev) => (prev + 1) % authors.length);
        pauseAutoScroll();
    };

    const shiftLeft = () => {
        setIndex((prev) => (prev - 1 + authors.length) % authors.length);
        pauseAutoScroll();
    };
    
    const pauseAutoScroll = () => {
        setIsPaused(true);
        setTimeout(() => setIsPaused(isPaused!), 5000);
    };

    return (
        <Flex overflow="hidden" justify="center" align="center" position="relative" width="100%" p="4">
            <Button
                onClick={shiftLeft}
                position="absolute"
                left="10px"
                zIndex="2"
                bg="white"
                boxShadow="md"
                _hover={{ bg: "gray.200" }}
            >
                <Icon as={MdChevronLeft} w="6" h="6" />
            </Button>

            <Box width="100%" px="60px" overflow="hidden">
                <Flex
                    as={motion.div}
                    initial={{ x: 0 }}
                    animate={{ x: 0 }}
                    animation={{ duration: 0.5, ease: "easeInOut" }}
                    justify="center"
                    align="center"
                    gap="16px"
                >
                    {indices.map((authorIndex, pos) => (
                        <AuthorCard key={authorIndex} author={authors[authorIndex]} position={pos} />
                    ))}
                </Flex>
            </Box>

            <Button
                onClick={shiftRight}
                position="absolute"
                right="10px"
                zIndex="2"
                bg="white"
                boxShadow="md"
                _hover={{ bg: "gray.200" }}
            >
                <Icon as={MdChevronRight} w="6" h="6" />
            </Button>
        </Flex>
    );
};

const AuthorsPage = () => (
    <Box
        height="100vh"
        width="100vw"
        backgroundImage="url('/images/forest.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        display="flex"
        justifyContent="center"
        alignItems="center"
    >
        <Box
            padding="5"
            textAlign="center"
            width='100%'
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <Heading
                bg="white"
                boxShadow="lg"
                borderRadius="md"
                padding="3"
                as="h2"
                size="lg"
                width='200px'
            >
                O zespole
            </Heading>
            <Box
                padding="3"
                width='100%'
            >
                <AuthorCarousel />
            </Box>
        </Box>
    </Box>
);

export default AuthorsPage;
